import React, { useEffect, useState, useCallback } from 'react';
import {
  Search, Plus, CheckCircle, Clock, XCircle, Briefcase,
  MapPin, Eye, X, ShieldCheck, ShieldX, FileText,
  Phone, Mail, Globe, Building2, User, ExternalLink,
  Calendar, Tag, Clock4, Trash2, AlertTriangle,
} from 'lucide-react';
import api from '../utils/api';

// ─── helper ──────────────────────────────────────────────
const DOC_LABEL = {
  aadhaar_document: 'AADHAAR',
  pan_document:     'PAN Card',
  shop_document:    'Shop & Establishment',
  gst_document:     'GST Certificate',
  trade_document:   'Trade License',
};

// Format "2026-05-12T..." → "5/12/2026"
const formatRegDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

// "Mon,Tue,Wed" → "Mon, Tue, Wed"
const formatDays = (days) => {
  if (!days) return '—';
  return String(days).split(',').map((s) => s.trim()).filter(Boolean).join(', ');
};

const Initials = ({ name, size = 44 }) => {
  const parts = (name || '').trim().split(' ');
  const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');
  return (
    <div className="biz-initials-avatar" style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {letters}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// SMALL UI ATOMS (Tailwind, reusable inside this modal)
// ═══════════════════════════════════════════════════════════
const InfoRow = ({ icon: Icon, label, value, mono = false, link = false, href }) => {
  const text = value || '—';
  return (
    <div className="flex items-start gap-2.5">
      {Icon && (
        <Icon size={14} className="mt-[3px] text-gray-400 shrink-0" aria-hidden="true" />
      )}
      <div className="flex-1 min-w-0">
        {label && (
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-0.5">
            {label}
          </p>
        )}
        {link && href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="block text-[13px] text-blue-600 hover:underline break-all"
          >
            {text}
          </a>
        ) : (
          <p
            className={[
              'text-[13px] text-gray-900 break-words',
              mono ? 'font-mono tracking-wide' : '',
            ].join(' ')}
          >
            {text}
          </p>
        )}
      </div>
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, action, children }) => (
  <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
    <div className="flex items-center justify-between gap-3 mb-3">
      <h4 className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
        {Icon && <Icon size={14} className="text-gray-500" />}
        {title}
      </h4>
      {action}
    </div>
    <div className="space-y-3">{children}</div>
  </section>
);

const StatusBadge = ({ verified }) =>
  verified ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-[11px] font-semibold">
      <CheckCircle size={11} /> Verified
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-[11px] font-semibold">
      <Clock size={11} /> Pending
    </span>
  );

// ═══════════════════════════════════════════════════════════
// DETAIL MODAL
// ═══════════════════════════════════════════════════════════
const BusinessDetailModal = ({ bizId, onClose, onVerifyChange, onRequestDelete }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [activeDocIdx, setActiveDocIdx] = useState(0);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await api.get(`/admin/businesses/${bizId}`);
        setDetail(res.data.data);
      } catch (err) {
        console.error('Fetch detail error:', err);
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [bizId]);

  const handleVerify = async () => {
    if (!detail) return;
    const newStatus = !detail.profile.is_verified;
    setVerifying(true);
    try {
      await api.patch(`/admin/businesses/${bizId}/verify`, { is_verified: newStatus });
      setDetail((prev) => ({
        ...prev,
        profile: { ...prev.profile, is_verified: newStatus },
      }));
      onVerifyChange(bizId, newStatus);
    } catch (err) {
      alert('Failed to update verification status');
    } finally {
      setVerifying(false);
    }
  };

  const isVerified = detail?.profile?.is_verified;
  const docs = detail?.documents || [];
  const activeDocs = docs.filter((d) => d.media_url);
  const currentDoc = activeDocs[activeDocIdx];
  const isPdf = currentDoc?.media_url?.toLowerCase().endsWith('.pdf');

  // Primary document number to always show in the sidebar.
  // Falls back across: profile column → first doc with a number → any doc number.
  const primaryDocNumber =
    detail?.profile?.document_number ||
    docs.find((d) => d.document_number)?.document_number ||
    null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 backdrop-blur-[2px] p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="biz-detail-title"
    >
      <div
        className="relative w-full sm:max-w-6xl bg-gray-50 sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-screen sm:max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 sm:px-6 py-4 bg-white border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <h2
              id="biz-detail-title"
              className="text-base sm:text-lg font-semibold text-gray-900 truncate"
            >
              Business Details
            </h2>
            {detail && <StatusBadge verified={isVerified} />}
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        {/* ── Body ── */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center min-h-[280px] text-sm text-gray-500">
            Loading details...
          </div>
        ) : !detail ? (
          <div className="flex-1 flex items-center justify-center min-h-[280px] text-sm text-gray-500">
            Failed to load. Please try again.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_1fr] xl:grid-cols-[minmax(0,400px)_1fr] gap-5 p-4 sm:p-6">

              {/* ════════════ LEFT SIDEBAR ════════════ */}
              <aside className="space-y-5 min-w-0">

                {/* Hero card */}
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-4">
                    {detail.logo_url ? (
                      <img
                        src={detail.logo_url}
                        alt={`${detail.profile.business_name} logo`}
                        className="w-16 h-16 rounded-xl object-cover ring-1 ring-gray-200"
                      />
                    ) : (
                      <Initials name={detail.profile.business_name} size={64} />
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-gray-900 truncate">
                        {detail.profile.business_name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {detail.profile.category || 'Uncategorized'}
                      </p>
                      <div className="mt-2">
                        <StatusBadge verified={isVerified} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Basic Information */}
                <SectionCard icon={Building2} title="Basic Information">
                  <InfoRow label="Business ID" value={`#${detail.profile.id}`} mono />
                  <InfoRow icon={Tag} label="Category" value={detail.profile.category} />
                  <InfoRow
                    icon={Calendar}
                    label="Registration Date"
                    value={formatRegDate(detail.profile.created_at)}
                  />
                  <InfoRow
                    icon={FileText}
                    label="Document Number"
                    value={primaryDocNumber}
                    mono
                  />
                </SectionCard>

                {/* Working Hours */}
                <SectionCard icon={Clock4} title="Working Hours">
                  <InfoRow
                    icon={Clock}
                    label="Hours"
                    value={`${detail.profile.opening_time || '—'} – ${detail.profile.closing_time || '—'}`}
                  />
                  <InfoRow
                    icon={Calendar}
                    label="Days"
                    value={formatDays(detail.profile.working_days)}
                  />
                </SectionCard>

                {/* Owner Information */}
                <SectionCard icon={User} title="Owner Information">
                  <InfoRow label="Name" value={detail.profile.owner_name} />
                  <InfoRow
                    icon={Mail}
                    label="Email"
                    value={detail.profile.owner_email}
                    link={!!detail.profile.owner_email}
                    href={detail.profile.owner_email ? `mailto:${detail.profile.owner_email}` : undefined}
                  />
                  <InfoRow
                    icon={Phone}
                    label="Phone"
                    value={detail.profile.owner_phone || detail.profile.contact_phone}
                  />
                </SectionCard>

                {/* Business Contact & Address */}
                <SectionCard icon={MapPin} title="Address & Contact">
                  {detail.profile.contact_phone && (
                    <InfoRow icon={Phone} label="Business Phone" value={detail.profile.contact_phone} />
                  )}
                  {detail.profile.contact_email && (
                    <InfoRow
                      icon={Mail}
                      label="Business Email"
                      value={detail.profile.contact_email}
                      link
                      href={`mailto:${detail.profile.contact_email}`}
                    />
                  )}
                  {detail.profile.website && (
                    <InfoRow
                      icon={Globe}
                      label="Website"
                      value={detail.profile.website}
                      link
                      href={detail.profile.website}
                    />
                  )}
                  {detail.profile.full_address && (
                    <InfoRow icon={MapPin} label="Address" value={detail.profile.full_address} />
                  )}
                  {!detail.profile.contact_phone &&
                    !detail.profile.contact_email &&
                    !detail.profile.website &&
                    !detail.profile.full_address && (
                      <p className="text-xs text-gray-400">No contact details on file.</p>
                    )}
                </SectionCard>
              </aside>

              {/* ════════════ MAIN CONTENT ════════════ */}
              <main className="space-y-5 min-w-0">

                {/* Document Verification */}
                <SectionCard
                  icon={FileText}
                  title="Document Verification"
                  action={
                    activeDocs.length > 0 && (
                      <span className="text-[11px] font-medium text-gray-500">
                        {activeDocs.length} document{activeDocs.length !== 1 ? 's' : ''}
                      </span>
                    )
                  }
                >
                  {/* Doc tabs */}
                  {activeDocs.length > 1 && (
                    <div className="flex flex-wrap gap-2 -mt-1">
                      {activeDocs.map((d, i) => (
                        <button
                          key={d.id}
                          onClick={() => setActiveDocIdx(i)}
                          className={[
                            'px-3 py-1.5 rounded-lg text-[12px] font-medium border transition',
                            activeDocIdx === i
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
                          ].join(' ')}
                        >
                          {DOC_LABEL[d.media_type] || d.media_type}
                        </button>
                      ))}
                    </div>
                  )}

                  {activeDocs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                        <FileText size={22} className="text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">No documents uploaded</p>
                      <p className="text-xs text-gray-500 mt-1">
                        The owner has not uploaded verification documents yet.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Meta grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl bg-gray-50 border border-gray-200 p-4">
                        <div>
                          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1">
                            Document Type
                          </p>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 text-[12px] font-semibold border border-blue-100">
                            {DOC_LABEL[currentDoc?.media_type] || currentDoc?.media_type}
                          </span>
                        </div>
                        {currentDoc?.document_number && (
                          <div>
                            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1">
                              Document Number
                            </p>
                            <p className="font-mono text-sm text-gray-900 tracking-wide break-all">
                              {currentDoc.document_number}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* View / Download button */}
                      <a
                        href={currentDoc?.media_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 transition shadow-sm"
                      >
                        <ExternalLink size={14} />
                        View Document
                      </a>

                      {/* Preview */}
                      {currentDoc?.media_url && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
                          {isPdf ? (
                            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                              <FileText size={36} className="text-gray-400 mb-3" />
                              <p className="text-sm font-medium text-gray-700">PDF document</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Click "View Document" above to open in a new tab.
                              </p>
                            </div>
                          ) : (
                            <img
                              src={currentDoc.media_url}
                              alt="Document preview"
                              className="block w-full max-h-[420px] object-contain bg-white"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </SectionCard>

                {/* Verification Status / Action */}
                <SectionCard icon={isVerified ? ShieldCheck : ShieldX} title="Verification Status">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div
                      className={[
                        'flex items-start gap-3 rounded-xl p-4 flex-1 min-w-0',
                        isVerified
                          ? 'bg-emerald-50 border border-emerald-200'
                          : 'bg-amber-50 border border-amber-200',
                      ].join(' ')}
                    >
                      {isVerified ? (
                        <ShieldCheck size={22} className="text-emerald-600 shrink-0 mt-0.5" />
                      ) : (
                        <ShieldX size={22} className="text-amber-600 shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0">
                        <p
                          className={[
                            'text-sm font-semibold',
                            isVerified ? 'text-emerald-900' : 'text-amber-900',
                          ].join(' ')}
                        >
                          {isVerified ? 'This business is Verified' : 'Verification Pending'}
                        </p>
                        <p
                          className={[
                            'text-xs mt-0.5',
                            isVerified ? 'text-emerald-700' : 'text-amber-700',
                          ].join(' ')}
                        >
                          {isVerified
                            ? 'Customers can see it as a trusted business.'
                            : 'Review the documents above before verifying.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </SectionCard>
              </main>
            </div>
          </div>
        )}

        {/* ── Footer actions (sticky) ── */}
        {detail && !loading && (
          <footer className="sticky bottom-0 z-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 px-5 sm:px-6 py-4 bg-white border-t border-gray-200">
            <button
              onClick={() =>
                onRequestDelete({
                  id: bizId,
                  business_name: detail.profile.business_name,
                  owner_name: detail.profile.owner_name,
                  owner_email: detail.profile.owner_email,
                })
              }
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold text-rose-700 bg-white border border-rose-300 hover:bg-rose-50 transition sm:mr-auto"
            >
              <Trash2 size={14} />
              Delete Business
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition"
            >
              Close
            </button>
            <button
              onClick={handleVerify}
              disabled={verifying}
              className={[
                'inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-[13px] font-semibold text-white shadow-sm transition',
                verifying ? 'opacity-70 cursor-not-allowed' : '',
                isVerified
                  ? 'bg-rose-600 hover:bg-rose-700'
                  : 'bg-emerald-600 hover:bg-emerald-700',
              ].join(' ')}
            >
              {isVerified ? <ShieldX size={14} /> : <ShieldCheck size={14} />}
              {verifying
                ? 'Updating...'
                : isVerified
                ? 'Unverify Business'
                : 'Verify Business'}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
const Businesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState({ total: 0, verified: 0, pending: 0, inactive: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [viewBizId, setViewBizId] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', password: '' });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchBusinesses = useCallback(async () => {
    try {
      const res = await api.get('/admin/businesses', {
        params: { search, status: statusFilter },
      });
      setBusinesses(res.data.data.businesses);
      setStats(res.data.data.stats);
    } catch (err) {
      console.error('Fetch businesses error:', err);
      setBusinesses([]);
      setStats({ total: 0, verified: 0, pending: 0, inactive: 0 });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(fetchBusinesses, 300);
    return () => clearTimeout(timer);
  }, [fetchBusinesses]);

  // Called from modal when verification status changes
  const handleVerifyChange = (id, newStatus) => {
    setBusinesses((prev) =>
      prev.map((b) => (b.id === id ? { ...b, is_verified: newStatus } : b))
    );
    setStats((prev) => ({
      ...prev,
      verified: newStatus ? prev.verified + 1 : Math.max(0, prev.verified - 1),
      pending: newStatus ? Math.max(0, prev.pending - 1) : prev.pending + 1,
    }));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError('');
    setDeleting(true);
    try {
      await api.delete(`/admin/businesses/${deleteTarget.id}`);
      const wasVerified = businesses.find((b) => b.id === deleteTarget.id)?.is_verified;
      setBusinesses((prev) => prev.filter((b) => b.id !== deleteTarget.id));
      setStats((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        verified: wasVerified ? Math.max(0, prev.verified - 1) : prev.verified,
        pending: wasVerified ? prev.pending : Math.max(0, prev.pending - 1),
      }));
      setDeleteTarget(null);
      // If the detail modal was open for this business, close it too.
      setViewBizId((cur) => (cur === deleteTarget.id ? null : cur));
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to delete business');
    } finally {
      setDeleting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      await api.post('/admin/users', { ...form, role: 'business_owner' });
      setShowRegisterModal(false);
      setForm({ full_name: '', email: '', phone: '', password: '' });
      fetchBusinesses();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create business owner');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrap">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Business Management</h1>
          <p className="page-subtitle">Manage and verify business listings</p>
        </div>
        <button className="btn-primary" onClick={() => setShowRegisterModal(true)} id="register-business-btn">
          <Plus size={16} /> Register Business
        </button>
      </div>

      {/* Search + Filter */}
      <div className="search-filter-row">
        <div className="search-wrap flex-1">
          <Search size={16} className="search-icon" />
          <input
            id="business-search"
            type="text"
            placeholder="Search by business name, ID, category, or owner..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>
        <select
          className="status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          id="business-status-filter"
        >
          <option value="">All Status</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Stats cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <Briefcase size={22} className="stat-icon blue" />
          <div>
            <p className="stat-label">Total Businesses</p>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="stat-card">
          <CheckCircle size={22} className="stat-icon green" />
          <div>
            <p className="stat-label">Verified</p>
            <p className="stat-value">{stats.verified}</p>
          </div>
        </div>
        <div className="stat-card">
          <Clock size={22} className="stat-icon orange" />
          <div>
            <p className="stat-label">Pending</p>
            <p className="stat-value">{stats.pending}</p>
          </div>
        </div>
        <div className="stat-card">
          <XCircle size={22} className="stat-icon red" />
          <div>
            <p className="stat-label">Inactive</p>
            <p className="stat-value">{stats.inactive}</p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card table-card">
        {loading ? (
          <div className="page-loading">Loading...</div>
        ) : (
          <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>BUSINESS</th>
                <th>OWNER</th>
                <th>CATEGORY</th>
                <th>LOCATION</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {businesses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-row">No businesses found</td>
                </tr>
              ) : (
                businesses.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <div className="biz-cell">
                        <p className="biz-name">{b.business_name}</p>
                        <p className="biz-id">ID: {b.id}</p>
                      </div>
                    </td>
                    <td>
                      <div className="biz-cell">
                        <p className="biz-owner-name">{b.owner_name}</p>
                        <a href={`mailto:${b.owner_email}`} className="contact-link small">{b.owner_email}</a>
                      </div>
                    </td>
                    <td>{b.category || '—'}</td>
                    <td>
                      <div className="location-cell">
                        <MapPin size={13} className="location-icon" />
                        <span>{[b.city, b.state].filter(Boolean).join(', ') || '—'}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${b.is_verified ? 'badge-verified' : 'badge-pending'}`}>
                        {b.is_verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {/* View button */}
                        <button
                          className="view-btn"
                          onClick={() => setViewBizId(b.id)}
                          title="View full details & documents"
                        >
                          <Eye size={14} />
                          View
                        </button>
                        {/* Delete button */}
                        <button
                          className="delete-btn"
                          onClick={() =>
                            setDeleteTarget({
                              id: b.id,
                              business_name: b.business_name,
                              owner_name: b.owner_name,
                              owner_email: b.owner_email,
                            })
                          }
                          title="Permanently delete business & owner account"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* ── Detail Modal ── */}
      {viewBizId && (
        <BusinessDetailModal
          bizId={viewBizId}
          onClose={() => setViewBizId(null)}
          onVerifyChange={handleVerifyChange}
          onRequestDelete={(target) => setDeleteTarget(target)}
        />
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteTarget && (
        <div
          className="modal-overlay"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-form">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                  <AlertTriangle size={22} className="text-rose-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-gray-900">
                    Delete this business permanently?
                  </h2>
                  <p className="text-[13px] text-gray-600 mt-1">
                    This will permanently delete{' '}
                    <span className="font-semibold text-gray-900">
                      {deleteTarget.business_name}
                    </span>{' '}
                    and the owner account
                    {deleteTarget.owner_email ? (
                      <>
                        {' '}(<span className="font-medium">{deleteTarget.owner_email}</span>)
                      </>
                    ) : null}
                    , including all profile data, documents, photos, reviews and
                    subscriptions. The email will be freed for reuse.{' '}
                    <span className="font-semibold text-rose-600">
                      This action cannot be undone.
                    </span>
                  </p>
                </div>
              </div>

              {deleteError && <div className="error-banner mt-4">{deleteError}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-[13px] font-semibold text-white bg-rose-600 hover:bg-rose-700 transition disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <Trash2 size={14} />
                  {deleting ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Register Modal ── */}
      {showRegisterModal && (
        <div className="modal-overlay" onClick={() => setShowRegisterModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Register Business Owner</h2>
              <button className="modal-close" onClick={() => setShowRegisterModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleRegister} className="modal-form">
              {formError && <div className="error-banner">{formError}</div>}
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" required value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" required value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Password *</label>
                <input className="form-input" type="password" required value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowRegisterModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Register'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Businesses;
